/**
 * Notification Store (Zustand + Firestore)
 *
 * Manages user notifications stored in `users/{uid}.notifications` array.
 * Used for level-up events, quiz unlocks, and news/system alerts. (`discount_earned`
 * is retired — older stored notifications may still carry it.)
 *
 * Kept separate from levelUpStore so notifications can be extended independently.
 */

import { create } from 'zustand';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { toDate } from '../utils/firestoreDate';
import { logger } from '../services/loggingService';
import { getErrorMessage } from '../utils/validation';
import type { UserNotification } from '../types/user';

// ── Helpers ──────────────────────────────────────────────────────────────────

function generateNotificationId(): string {
  return `notif_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── Store ────────────────────────────────────────────────────────────────────

interface NotificationState {
  notifications: UserNotification[];
  unreadCount: number;
  loaded: boolean;

  loadNotifications: (userId: string) => Promise<void>;
  addNotification: (
    userId: string,
    notif: Omit<UserNotification, 'id' | 'createdAt' | 'read'>,
  ) => Promise<void>;
  markRead: (userId: string, notificationId: string) => Promise<void>;
  markAllRead: (userId: string) => Promise<void>;
  deleteNotification: (userId: string, notificationId: string) => Promise<void>;
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loaded: false,

  loadNotifications: async (userId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        set({ loaded: true });
        return;
      }

      const data = userDoc.data();
      // A `Date` written into Firestore comes back as a `Timestamp`, and
      // `new Date(timestamp)` is `Invalid Date` — which is what every row in
      // the panel rendered, and why the sort below was arbitrary ([[BUGS #156]]).
      const raw: UserNotification[] = (data.notifications ?? []).map(
        (n: UserNotification) => ({ ...n, createdAt: toDate(n.createdAt) }),
      );

      // Sort newest first
      const sorted = [...raw].sort(
        (a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime(),
      );
      const unread = sorted.filter((n) => !n.read).length;

      set({ notifications: sorted, unreadCount: unread, loaded: true });
    } catch (err) {
      logger.error('Failed to load notifications', {
        error: getErrorMessage(err),
      });
      set({ loaded: true });
    }
  },

  addNotification: async (userId, notif) => {
    // De-duplication only means something against the STORED list. Until the
    // load resolves, `notifications` is empty, so every early add looked unique
    // and wrote another copy — and `arrayUnion` cannot collapse them either,
    // because each carries its own `createdAt`. The level-1 trigger fires from
    // a mount effect, so this race was the normal case, not the edge one
    // ([[BUGS #156]]). Load first when we have not yet.
    if (!get().loaded) {
      await get().loadNotifications(userId);
    }
    const existing = get().notifications;
    const isDuplicate = existing.some(
      (n) => n.type === notif.type && n.title === notif.title,
    );
    if (isDuplicate) return;

    const newNotification: UserNotification = {
      ...notif,
      id: generateNotificationId(),
      read: false,
      createdAt: new Date(),
    };

    // Optimistic update
    const previousNotifications = get().notifications;
    const previousUnreadCount = get().unreadCount;
    set((state) => ({
      notifications: [newNotification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
    }));

    try {
      await updateDoc(doc(db, 'users', userId), {
        notifications: arrayUnion(newNotification),
      });
    } catch (err) {
      // Rollback optimistic update on failure
      set({ notifications: previousNotifications, unreadCount: previousUnreadCount });
      logger.error('Failed to add notification', {
        error: getErrorMessage(err),
      });
    }
  },

  markRead: async (userId, notificationId) => {
    const { notifications, unreadCount } = get();
    const updated = notifications.map((n) =>
      n.id === notificationId ? { ...n, read: true } : n,
    );
    const newUnread = updated.filter((n) => !n.read).length;

    set({ notifications: updated, unreadCount: newUnread });

    try {
      await updateDoc(doc(db, 'users', userId), {
        notifications: updated,
      });
    } catch (err) {
      set({ notifications, unreadCount });
      logger.error('Failed to mark notification read', {
        error: getErrorMessage(err),
      });
    }
  },

  markAllRead: async (userId) => {
    const { notifications, unreadCount } = get();
    const updated = notifications.map((n) => ({ ...n, read: true }));

    set({ notifications: updated, unreadCount: 0 });

    try {
      await updateDoc(doc(db, 'users', userId), {
        notifications: updated,
      });
    } catch (err) {
      set({ notifications, unreadCount });
      logger.error('Failed to mark all notifications read', {
        error: getErrorMessage(err),
      });
    }
  },

  deleteNotification: async (userId, notificationId) => {
    const { notifications, unreadCount } = get();
    const updated = notifications.filter((n) => n.id !== notificationId);
    const newUnread = updated.filter((n) => !n.read).length;

    set({ notifications: updated, unreadCount: newUnread });

    try {
      await updateDoc(doc(db, 'users', userId), {
        notifications: updated,
      });
    } catch (err) {
      set({ notifications, unreadCount });
      logger.error('Failed to delete notification', {
        error: getErrorMessage(err),
      });
    }
  },

  reset: () => {
    set({ notifications: [], unreadCount: 0, loaded: false });
  },
}));
